import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '极速影视',
    url: 'https://www.jisushipin.com/',
    description: '极速影视为您汇集最新上线电影、热播电视剧全集和高清综艺节目单，全部免费在线观看不缓冲。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jisushipin.com',
      telephone: '+86-400-888-8888',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '影视产业园区88号',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '极速影视的资源来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '极速影视与多家内容提供商合作，为用户聚合优质的影视资源。我们致力于为用户提供最新、最全的影视内容推荐服务。',
        },
      },
      {
        '@type': 'Question',
        name: '极速影视的影片多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '极速影视每日更新最新电影和电视剧资源，确保用户第一时间获取最新上线的影视内容。电影频道紧跟院线动态，电视剧频道每日同步最新剧集。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系极速影视客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：发送邮件至contact@jisushipin.com，或拨打客服电话400-888-8888（工作日9:00-18:00），我们将竭诚为您服务。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mt-4">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                了解极速影视的品牌故事与使命
              </p>
            </div>
          </div>

          <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-10 md:py-16">
            <section className="mb-14" aria-labelledby="brand-intro">
              <h2 id="brand-intro" className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-5">
                品牌介绍
              </h2>
              <div className="prose prose-sm max-w-none">
                <p className="text-foreground-700 leading-relaxed mb-4">
                  极速影视成立于2020年，是一家专注于影视内容聚合与推荐的在线平台。自成立以来，极速影视始终秉持
                  <strong>"汇聚精彩，传递感动"</strong>的品牌理念，致力于为广大影视爱好者提供最新、最全、最优质的影视内容导航服务。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  极速影视平台汇集了最新上线电影、热播电视剧全集和高清综艺节目单，涵盖科幻、动作、喜剧、古装、仙侠、悬疑、动漫等多种类型。无论您是电影发烧友、追剧达人还是综艺爱好者，都能在极速影视找到心仪的精彩内容。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  极速影视的编辑团队由一群资深的影视行业从业者和内容运营专家组成，他们每天用心筛选和推荐优质内容，确保极速影视的用户能够第一时间获取最新、最好看的影视资讯。极速影视最新电影大全每日更新，追剧不停，高清不卡，为用户带来极致流畅的观影体验。
                </p>
              </div>
            </section>

            <section className="mb-14" aria-labelledby="reputation">
              <h2 id="reputation" className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-5">
                品牌口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-6">
                <div className="p-5 md:p-6 rounded-xl bg-background-100 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">5000万+</div>
                  <p className="text-sm text-foreground-600">累计服务用户</p>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">10万+</div>
                  <p className="text-sm text-foreground-600">收录影视作品</p>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">99.7%</div>
                  <p className="text-sm text-foreground-600">用户满意度</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 text-accent-500 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-2">
                    "极速影视的更新速度真的很快，每次有新电影上线都能第一时间看到。界面也很清爽，找片子很方便，推荐给身边很多朋友了！"
                  </p>
                  <p className="text-xs text-foreground-500">—— 资深影迷 张先生</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 text-accent-500 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-2">
                    "作为一个追剧狂人，极速影视简直就是我的追剧神器。分类清晰，更新及时，每天都要来逛一圈看看有什么新剧上线。"
                  </p>
                  <p className="text-xs text-foreground-500">—— 追剧达人 李女士</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 text-accent-500 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-2">
                    "极速影视的综艺频道是我最喜欢的，节目单很清楚，每周追哪些综艺一目了然。高清不卡，体验真的很棒！"
                  </p>
                  <p className="text-xs text-foreground-500">—— 综艺爱好者 王先生</p>
                </div>
              </div>
            </section>

            <section aria-labelledby="contact">
              <h2 id="contact" className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-5">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">contact@jisushipin.com</p>
                  </div>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">客服电话</h3>
                    <p className="text-sm text-foreground-600">400-888-8888</p>
                  </div>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">公司地址</h3>
                    <p className="text-sm text-foreground-600">北京市朝阳区影视产业园区88号</p>
                  </div>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">微信公众号</h3>
                    <p className="text-sm text-foreground-600">极速影视官方</p>
                  </div>
                </div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">官方微博</h3>
                    <p className="text-sm text-foreground-600">@极速影视</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}